/**
 * AI-JAVADOC: WfProcessStepActivityUfFormComponent
 * Module: WF (Workflow)
 * Type: Protected Component - Process Activity
 * Description: Workflow activity component that embeds a UF (Universal Forms) form renderer.
 *              Creates or loads a UF form based on the activity configuration (formTypeDtoId),
 *              processes import bindings (startObject, f_* fields), and stores the completed
 *              form result via WfProcessRuntimeService for the parent WF process to consume.
 */
import {Component, OnChanges, OnDestroy, OnInit} from '@angular/core';
import {
    WfProcessStepActivityBaseComponent
} from '../wf-process-step-activity-base/wf-process-step-activity-base.component';
import {WfProcessRuntimeBindingDto} from '../../../../model/dto/api/wf-process-runtime-binding.dto';
import {WfProcessRuntimeBindingUtil} from '../../../../logic/wf-process-runtime-binding.util';
import {WfProcessRuntimeService} from '../../../../service/wf-process-runtime.service';
import {WfActivityProcessingResultFieldDto} from '../../../../model/dto/api/wf-activity-processing-result-field.dto';
import {WfBindingFieldActionEnum} from '../../../../enum/wf-binding-field-action.enum';
import {WfProcessStepActivityEvent} from '../../../../logic/wf-process-step-activity.event';
import {UfFormRuntimeDto} from '../../../../../../feature-core/uf/model/dto/api/uf-form-runtime.dto';
import {
    UfFormRuntimeService
} from '../../../../../../feature-core/uf/service/domain/uf-form-runtime/uf-form-runtime.service';
import {UfFormCreateRequestDto} from '../../../../../../feature-core/uf/model/dto/api/uf-form-create-request.dto';
import {MvsCoreService} from '../../../../../../core/shared/service/mvs-core.service';
import {MvsMessageService} from '../../../../../../core/shared/service/message/mvs-message.service';

@Component({
    selector: 'mvs-wf-process-step-activity-uf-form',
    templateUrl: './wf-process-step-activity-uf-form.component.html',
    styleUrls: ['./wf-process-step-activity-uf-form.component.scss'],
    standalone: false
})
export class WfProcessStepActivityUfFormComponent
    extends WfProcessStepActivityBaseComponent
    implements OnInit, OnChanges, OnDestroy {

    ufFormId: number;
    ufFormRuntime: UfFormRuntimeDto;
    formCreationError: boolean = false;

    private constBindingFieldPrefix: string = 'f_';

    constructor(
        protected coreService: MvsCoreService,
        protected messageService: MvsMessageService,
        private formRuntimeService: UfFormRuntimeService) {

        super(coreService, messageService);
    }

    /**
     * Refresh Component.
     * Reads import bindings and creates/loads the UF form.
     */
    refreshComponent() {
        this.busy = true;
        this.formCreationError = false;

        // Extract startObject binding (if present)
        const startObjectBinding = WfProcessRuntimeBindingUtil.deriveBinding(this.bindings, 'startObject');

        // Build import objects map from startObject and f_* bindings
        const importObjects: { [key: string]: number } = {};

        if (startObjectBinding && startObjectBinding.objectBinding && startObjectBinding.objectId) {
            importObjects['startObject'] = startObjectBinding.objectId;
        }

        // Process f_* field bindings for import objects
        if (this.bindings && this.bindings.length) {
            for (const binding of this.bindings) {
                if (binding.alias.startsWith(this.constBindingFieldPrefix)
                    && binding.objectBinding && binding.objectId) {
                    const fieldName = binding.alias.slice(this.constBindingFieldPrefix.length);
                    importObjects[fieldName] = binding.objectId;
                }
            }
        }

        // Determine formTypeId from activity configuration
        const formTypeId = this.activityType.activityUfFormDto.formTypeDtoId;

        // Create a new UF form
        const createRequest: UfFormCreateRequestDto = {
            formTypeId: formTypeId,
            importObjects: Object.keys(importObjects).length > 0 ? importObjects : undefined
        };

        this.formRuntimeService.createForm(createRequest).subscribe({
            next: (runtime: UfFormRuntimeDto) => {
                this.ufFormId = runtime.formId;
                this.ufFormRuntime = runtime;
                this.busy = false;
                this.initialized = true;
            },
            error: () => {
                this.formCreationError = true;
                this.busy = false;
                this.initialized = true;
            }
        });
    }

    /**
     * Handle form saved event from the UF form renderer.
     * Builds processing result and stores it via WfProcessRuntimeService.
     * @param formData The saved form field values.
     */
    onFormSaved(formData: { [key: string]: any }): void {
        const results: WfActivityProcessingResultFieldDto[] = [];

        // Store the completed form reference as result
        results.push({
            alias: 'ufFormCompleted',
            action: WfBindingFieldActionEnum.created,
            valueObjectTypeAlias: 'uf.UfForm',
            valueObjectId: this.ufFormId
        });

        // Store individual field values as simple value results
        if (formData) {
            for (const key of Object.keys(formData)) {
                if (formData[key] !== null && formData[key] !== undefined) {
                    results.push({
                        alias: this.constBindingFieldPrefix + key,
                        action: WfBindingFieldActionEnum.created,
                        simpleValue: formData[key]
                    });
                }
            }
        }

        WfProcessRuntimeService.setResult(results);
    }

    /**
     * Handle form cancelled event from the UF form renderer.
     * If the activity is not mandatory, emits onSkip. Otherwise shows a message.
     */
    onFormCancelled(): void {
        if (!this.activityType.activityUfFormDto.mandatoryActivity) {
            this.onSkip.emit(new WfProcessStepActivityEvent(null, this.activityType));
        } else {
            this.messageService.showInfoMessage(
                'Pflichtformular',
                'Dieses Formular ist ein Pflichtfeld und muss ausgefüllt werden.'
            );
        }
    }
}
